import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader } from "@/components/common/loader";
import { FIRM_FEED_LIMIT, listFirmCases } from "@/features/cases/api";
import { cn } from "@/lib/utils";

interface PriorityTask {
  id: string;
  clientId: string;
  caseId: string;
  clientName: string;
  resolution: string;
  description: string;
  dueDate: string | null;
}

/** How many open tasks the card shows before collapsing the rest into a count. */
const VISIBLE = 8;

/** Colour + label + sort weight for a task's due date, relative to today. */
function describeDue(dueDate: string | null): { label: string; dot: string; sortKey: number } {
  if (!dueDate) return { label: "No due date", dot: "bg-muted-foreground", sortKey: Infinity };
  // `due_date` is a date-only string ("YYYY-MM-DD"); `new Date(str)` would parse
  // it as UTC midnight and shift back a day when read in a US timezone. Build a
  // local-midnight date from the parts instead so the day never drifts.
  const [year, month, day] = dueDate.slice(0, 10).split("-").map(Number);
  if (year == null || month == null || day == null) {
    return { label: "No due date", dot: "bg-muted-foreground", sortKey: Infinity };
  }
  const due = new Date(year, month - 1, day);
  if (Number.isNaN(due.getTime())) {
    return { label: "No due date", dot: "bg-muted-foreground", sortKey: Infinity };
  }
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((due.getTime() - startToday.getTime()) / 86_400_000);

  if (diffDays < 0) {
    const days = Math.abs(diffDays);
    return { label: `${days}d overdue`, dot: "bg-destructive", sortKey: diffDays };
  }
  if (diffDays === 0) return { label: "Today", dot: "bg-destructive", sortKey: 0 };
  if (diffDays === 1) return { label: "Tomorrow", dot: "bg-warning", sortKey: 1 };
  const label = due.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return { label, dot: diffDays <= 7 ? "bg-warning" : "bg-primary", sortKey: diffDays };
}

/**
 * "Today's priorities" — every open task (case step) across all of the firm's
 * clients, surfaced most-urgent-first so the practitioner sees what needs
 * attention without opening each client. Tasks are case steps, so each row
 * deep-links to its parent case.
 */
export function TodaysPriorities() {
  const [tasks, setTasks] = useState<PriorityTask[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    listFirmCases("open")
      .then((res) => {
        if (!active) return;
        setTruncated(res.truncated);
        const open: PriorityTask[] = [];
        for (const c of res.cases) {
          for (const step of c.steps) {
            if (step.isCompleted) continue;
            open.push({
              id: step.id,
              clientId: c.client,
              caseId: c.id,
              clientName: c.clientName,
              resolution: c.resolutionTypeDisplay,
              description: step.description,
              dueDate: step.dueDate,
            });
          }
        }
        open.sort((a, b) => describeDue(a.dueDate).sortKey - describeDue(b.dueDate).sortKey);
        setTasks(open);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  const dueCount = tasks?.filter((t) => describeDue(t.dueDate).sortKey <= 0).length ?? 0;
  const visible = tasks?.slice(0, VISIBLE) ?? [];
  const overflow = (tasks?.length ?? 0) - visible.length;

  return (
    <Card className="h-full gap-0 overflow-hidden py-0">
      <CardHeader className="flex flex-row items-center justify-between border-b px-6 py-5">
        <CardTitle className="font-heading text-xl">Today</CardTitle>
        <div className="flex items-center gap-3">
          {dueCount > 0 && (
            <Badge className="bg-destructive/15 text-destructive border-transparent text-[11px] font-bold">
              {dueCount} DUE
            </Badge>
          )}
          <Link to="/search" className="text-info text-[13px] font-semibold hover:underline">
            Plan my day
          </Link>
        </div>
      </CardHeader>
      <CardContent className="px-6 py-6">
        {tasks === null && !error ? (
          <div className="flex justify-center py-6">
            <Loader />
          </div>
        ) : error ? (
          <p className="text-muted-foreground py-6 text-center text-sm">Couldn't load tasks.</p>
        ) : visible.length === 0 ? (
          <div className="border-border bg-surface-hover flex items-center gap-5 rounded-lg border border-dashed px-6 py-5">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true" className="shrink-0">
              <circle cx="28" cy="28" r="26" stroke="#2457E6" strokeOpacity="0.18" strokeWidth="2" />
              <circle
                cx="28"
                cy="28"
                r="26"
                stroke="#2457E6"
                strokeWidth="2"
                strokeDasharray="120 44"
                strokeLinecap="round"
                transform="rotate(-90 28 28)"
              />
              <path
                d="M19 28.5l6 6 12-13"
                stroke="#F28C28"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex flex-col gap-1">
              <span className="text-[15px] font-semibold">You're all caught up.</span>
              <span className="text-muted-foreground text-[13px]">
                No open tasks. New priorities will appear here as cases, notices and signatures come
                in.
              </span>
            </div>
          </div>
        ) : (
          <ul>
            {visible.map((task) => {
              const due = describeDue(task.dueDate);
              return (
                <li key={task.id}>
                  <Link
                    to={`/client/${task.clientId}/cases/${task.caseId}`}
                    className="hover:bg-accent -mx-4 flex items-center gap-3 border-b px-4 py-3 transition-colors last:border-0"
                  >
                    <span className={cn("size-2.5 shrink-0 rounded-full", due.dot)} />
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-sm font-semibold">
                        {task.description} — {task.clientName}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">{task.resolution}</p>
                    </div>
                    <span className="text-muted-foreground shrink-0 text-xs">{due.label}</span>
                  </Link>
                </li>
              );
            })}
            {overflow > 0 && (
              <li className="text-muted-foreground py-3 text-center text-xs">
                +{overflow} more open {overflow === 1 ? "task" : "tasks"}
              </li>
            )}
            {truncated && (
              <li className="text-muted-foreground py-3 text-center text-xs">
                Showing tasks from the first {FIRM_FEED_LIMIT} cases — some may be omitted.
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
